import {
  Alert,
  Amount,
  Box,
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RupeesIcon,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import { useEffect, useState } from 'react';
import useVPF from '../hooks/VPF/useVPF';
import { toIndianCurrencyString } from 'utils/Numbers';

interface VPFModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onOptIn: (amount: string) => void;
  onModify: (amount: string) => void;
  isEdit?: boolean;
}

const VPFModal = ({ isOpen, onDismiss, onOptIn, onModify, isEdit }: VPFModalProps) => {
  // Get data from useVPF hook instead of props
  const { firstName, isAdmin, vpfData } = useVPF();

  const [contributionAmount, setContributionAmount] = useState(
    vpfData?.vpfAmount ? vpfData.vpfAmount.toString() : '0',
  );
  const [contributionError, setContributionError] = useState('');
  const [isConsentChecked, setIsConsentChecked] = useState(false);

  useEffect(() => {
    setContributionAmount(vpfData?.vpfAmount ? vpfData.vpfAmount.toString() : '0');
  }, [isOpen, vpfData]);

  const onSubmit = () => {
    if (
      contributionAmount &&
      isConsentChecked &&
      vpfData?.maximumAllowedAmount &&
      Number(contributionAmount) <= vpfData?.maximumAllowedAmount
    ) {
      if (isEdit) {
        onModify(contributionAmount);
      } else {
        onOptIn(contributionAmount);
      }
      onDismiss();
    } else {
      setContributionError(
        `Contribution amount cannot be more than ${toIndianCurrencyString(
          Number(vpfData?.maximumAllowedAmount.toFixed(0) ?? 0),
        )} (88% of PF wages)`,
      );
    }
  };

  const onContributionAmountChange = ({ value }: { value: string }) => {
    setContributionAmount(value || '');
    setContributionError('');
  };

  const handleCancel = () => {
    setContributionAmount('');
    setIsConsentChecked(false);
    onDismiss();
  };

  const isOptInDisabled = !contributionAmount || !isConsentChecked;

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      size="medium"
      accessibilityLabel="Voluntary PF modal">
      <ModalHeader
        title={isEdit ? 'Modify VPF Amount' : `Voluntary PF${firstName ? ` for ${firstName}` : ''}`}
        subtitle={
          isAdmin
            ? "Voluntarily contribute more to employee's PF to maximise their savings."
            : 'Voluntarily contribute more to your PF to maximize your savings.'
        }
      />

      <ModalBody padding="spacing.6">
        <Box
          padding={'spacing.8'}
          display="flex"
          flexDirection="column"
          gap="spacing.5"
          backgroundColor={'surface.background.gray.moderate'}
          borderRadius={'large'}>
          <Box display="flex" flexDirection="column" gap="spacing.4" marginBottom={'spacing.8'}>
            <TextInput
              label="Monthly Contribution"
              necessityIndicator={'required'}
              placeholder="Enter amount"
              size="medium"
              leadingIcon={RupeesIcon}
              value={contributionAmount}
              onChange={(e) => onContributionAmountChange({ value: e.value || '' })}
              data-testid="vpf-contribution-amount"
              errorText={contributionError}
              validationState={contributionError ? 'error' : 'none'}
            />
            <Box overflow={'hidden'} borderRadius={'medium'}>
              <Alert
                description={
                  <Box display="flex" flexDirection="row" gap="spacing.2" alignItems="center">
                    <Text size="small" weight="regular" color="surface.text.gray.muted">
                      Declaration can be maximum of{' '}
                    </Text>
                    <Amount
                      color="surface.text.gray.muted"
                      suffix={'none'}
                      value={Number(vpfData?.maximumAllowedAmount.toFixed(0) ?? 0)}
                    />
                    <Text size="small" weight="regular" color="surface.text.gray.muted">
                      (88% of PF wages)
                    </Text>
                  </Box>
                }
                color="information"
                emphasis="subtle"
                isFullWidth={true}
                isDismissible={false}
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="row" gap="spacing.3" alignItems="flex-start">
            <Checkbox
              isChecked={isConsentChecked}
              onChange={(e) => setIsConsentChecked(e.isChecked)}
              testID="vpf-consent-checkbox"
            />
            <Text size="medium" color="surface.text.gray.subtle">
              {isAdmin
                ? 'Employee has agreed to VPF contribution. Deduction will apply from monthly salary.'
                : 'I want to contribute to VPF and understand that will be deducted from my monthly salary.'}
            </Text>
          </Box>
        </Box>
      </ModalBody>

      <ModalFooter>
        <Box display="flex" flexDirection="row" gap="spacing.5" justifyContent={'flex-end'}>
          <Button
            variant="tertiary"
            size="medium"
            onClick={handleCancel}
            data-testid="vpf-cancel-button">
            Cancel
          </Button>
          <Button variant="primary" size="medium" isDisabled={isOptInDisabled} onClick={onSubmit}>
            {isEdit ? 'Modify' : 'Opt in'}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default VPFModal;
